import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ParticleField, MorphBlob } from "@/components/effects/DynamicBackgrounds";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import { SEOHead, buildArticleLD } from "@/components/seo/SEOHead";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch {
        // Post not found - handled by null check below
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#020617] pt-20 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="animate-pulse">
            <div className="h-8 bg-[#111F36] rounded w-3/4 mb-4" />
            <div className="h-4 bg-[#111F36] rounded w-1/4 mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-[#111F36] rounded w-full" />
              <div className="h-4 bg-[#111F36] rounded w-full" />
              <div className="h-4 bg-[#111F36] rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-[#020617] pt-20 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-2xl font-bold text-[#F8FAFC] mb-4">Post Not Found</h1>
          <Link to="/insights" className="text-[#00B4D8] hover:underline">
            ← Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#020617] pt-20 min-h-screen relative overflow-hidden">
      {post && (
        <SEOHead
          title={post.title}
          description={post.excerpt}
          path={`/insights/${slug}`}
          type="article"
          article={{ publishedAt: post.published_at, author: post.author, tags: post.tags }}
          jsonLd={buildArticleLD({ title: post.title, description: post.excerpt, slug, author: post.author, publishedAt: post.published_at, tags: post.tags })}
        />
      )}
      <ParticleField count={12} className="opacity-30" />
      <MorphBlob color="rgba(0,180,216,0.04)" size="400px" className="top-[20%] right-[-10%]" />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Link */}
          <Link 
            to="/insights" 
            className="inline-flex items-center gap-2 text-[#00B4D8] hover:gap-3 transition-all mb-8"
            data-testid="back-to-insights"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Insights
          </Link>

          {/* Category Badge */}
          <div className="mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#00B4D8]/20 text-[#00B4D8]">
              {post.category.replace("_", " ")}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-[#94A3B8] mb-8 pb-8 border-b border-[#00B4D8]/10">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>

          {/* Excerpt */}
          <p className="text-lg text-[#94A3B8] mb-8 leading-relaxed italic border-l-4 border-[#00B4D8] pl-4">
            {post.excerpt}
          </p>

          {/* Content */}
          <div className="prose prose-invert max-w-none">
            <p className="text-[#94A3B8] leading-relaxed whitespace-pre-line">
              {post.content}
            </p>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-[#00B4D8]/10">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-[#64748B]" />
                {post.tags.map((tag) => (
                  <span 
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-[#111F36] text-[#94A3B8]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </article>
    </div>
  );
};

export default BlogPostPage;
